/**
 * Points configuration — the values the model runs on. Deliberately not
 * persisted and not editable: an earlier version saved to localStorage, and once
 * the business settled the supplement basis, stale saves silently overrode the
 * agreed values with no UI to reset them. A context rather than a direct import,
 * so only this file changes when the values move to the API.
 *
 * showPoints is a per-browser view preference, owned by TandemConsole alongside
 * hiddenPages — the console needs it to gate the Points model page itself.
 */
import { createContext, useContext } from "react";
import type { ReactNode } from "react";
import { DEFAULT_POINTS_CONFIG } from "./points";
import type { PointsConfig } from "./points";

interface PointsConfigValue {
    config: PointsConfig;
    /** False hides every points figure in the console and in the CSV. */
    showPoints: boolean;
}

const PointsConfigContext = createContext<PointsConfigValue>({
    config: DEFAULT_POINTS_CONFIG,
    showPoints: true,
});

export const PointsConfigProvider = ({
    showPoints = true,
    children,
}: {
    showPoints?: boolean;
    children: ReactNode;
}) => (
    <PointsConfigContext.Provider value={{ config: DEFAULT_POINTS_CONFIG, showPoints }}>
        {children}
    </PointsConfigContext.Provider>
);

export const usePointsConfig = () => useContext(PointsConfigContext);
